import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

async function fetchDocuments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_documents?course_id=eq.${courseId}&deleted=eq.false&select=*&order=uploaded_at.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function createDocument(courseId, title, fileName) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_documents`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json', 'Prefer': 'return=representation'
      },
      body: JSON.stringify({ course_id: courseId, title, drive_file_name: fileName, deleted: false })
    }
  );
  return response.json();
}

async function deleteDocument(id) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/course_documents?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ deleted: true })
    }
  );
}

export default function CourseDocuments({ course }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchDocuments(course.id)
      .then(data => setDocuments(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, [course.id]);

  async function handleUpload(file) {
    if (!file) return;
    if (!newTitle.trim()) { setError('Enter a title before uploading.'); return; }
    setUploading(true);
    setError('');
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result.split(',')[1];
        const mimeType = file.type;
        const fileName = file.name;

        const result = await createDocument(course.id, newTitle.trim(), fileName);
        console.log('createDocument result:', result);
        if (!Array.isArray(result) || !result[0]) throw new Error('Failed to create document record.');
        const doc = result[0];

        await fetch(API_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'uploadCourseDocument',
            documentId: doc.id,
            courseId: course.id,
            title: newTitle.trim(),
            fileName,
            base64Data,
            mimeType
          })
        });

        let fileId = null;
        for (let i = 0; i < 10; i++) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          const checkResponse = await fetch(
            `${SUPABASE_URL}/rest/v1/course_documents?id=eq.${doc.id}&select=drive_file_id`,
            { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
          );
          const checkData = await checkResponse.json();
          if (checkData[0]?.drive_file_id) {
            fileId = checkData[0].drive_file_id;
            doc.drive_file_id = fileId;
            break;
          }
        }

        setDocuments(prev => [...prev, doc]);
        setNewTitle('');
        setSuccess(fileId ? 'Document uploaded.' : 'Document added — file still uploading.');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError(err.message || 'Failed to upload document.');
      setUploading(false);
    }
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    await deleteDocument(doc.id);
    setDocuments(prev => prev.filter(d => d.id !== doc.id));
    setSuccess('Document deleted.');
  }

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <h2 style={styles.subheading}>Documents</h2>
      <p style={styles.muted}>Upload course documents for students to download.</p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {documents.length === 0 && (
        <div style={styles.muted}>No documents uploaded yet.</div>
      )}

      <div style={styles.list}>
        {documents.map(doc => (
          <div key={doc.id} style={styles.item}>
            <div style={styles.itemLeft}>
              <span style={styles.docIcon}>📄</span>
              <div>
                <div style={styles.docTitle}>{doc.title}</div>
                <div style={styles.docFile}>{doc.drive_file_name}</div>
              </div>
            </div>
            <div style={styles.itemRight}>
              {doc.drive_file_id ? (
                
                  <a href={`https://drive.google.com/uc?export=download&id=${doc.drive_file_id}`}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.downloadBtn}
                >
                  Download
                </a>
              ) : (
                <span style={styles.muted}>Processing...</span>
              )}
              <button style={styles.dangerBtn} onClick={() => handleDelete(doc)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.uploadForm}>
        <h3 style={styles.uploadHeading}>Upload Document</h3>
        <div style={styles.uploadRow}>
          <input
            style={styles.input}
            type="text"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="Document title"
          />
          <label style={styles.uploadLabel}>
            <input
              type="file"
              style={{ display: 'none' }}
              onChange={e => handleUpload(e.target.files[0])}
              disabled={uploading}
            />
            <span style={{ ...styles.uploadBtn, opacity: newTitle.trim() ? 1 : 0.5 }}>
              {uploading ? 'Uploading...' : '↑ Upload file'}
            </span>
          </label>
        </div>
        {!newTitle.trim() && <div style={styles.hint}>Enter a title first</div>}
      </div>
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 16px' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  list: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' },
  item: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', border: '1px solid #d7dee5', borderRadius: '10px',
    background: 'white', gap: '16px'
  },
  itemLeft: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1 },
  docIcon: { fontSize: '24px', flexShrink: 0 },
  docTitle: { fontWeight: '700', fontSize: '14px', color: '#1f2933' },
  docFile: { fontSize: '12px', color: '#66788a', marginTop: '2px' },
  itemRight: { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 },
  downloadBtn: {
    background: '#e7f1f7', color: '#215b7d', borderRadius: '6px',
    padding: '5px 10px', fontSize: '13px', textDecoration: 'none', fontWeight: '600'
  },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  uploadForm: {
    border: '1px solid #d7dee5', borderRadius: '10px', padding: '16px', background: '#f7f9fa'
  },
  uploadHeading: { margin: '0 0 12px', fontSize: '15px', color: '#1f2933' },
  uploadRow: { display: 'flex', gap: '10px', alignItems: 'center' },
  input: {
    flex: 1, border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', background: 'white'
  },
  uploadLabel: { cursor: 'pointer', flexShrink: 0 },
  uploadBtn: {
    background: '#215b7d', color: 'white', borderRadius: '8px',
    padding: '9px 14px', fontSize: '13px', fontWeight: '600',
    display: 'inline-block', cursor: 'pointer'
  },
  hint: { fontSize: '12px', color: '#a12a2a', marginTop: '6px' }
};